import { useMemo, useState } from 'react';
import {
  KeyIcon,
  DocumentTextIcon,
  BuildingLibraryIcon,
  ComputerDesktopIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChatBubbleOvalLeftIcon,
  PhoneIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

/**
 * Preguntas Frecuentes — Claude Design · PreguntasFrecuentes.dc.html
 *
 * Rediseño visual sobre el mismo dataset: hero de búsqueda con gradiente
 * ink→púrpura, sidebar sticky de categorías + columna principal con secciones
 * de accordion, y card de ayuda al pie. Se conservan las 6 categorías y sus
 * items (Firma Electrónica, Documento de Apoderado, Facturación Electrónica y
 * DGI, Uso del Sistema, Planes y Funcionalidades, Demo y Soporte), la búsqueda
 * client-side y el fallback "Respuesta pendiente de documentar" para items sin
 * respuesta.
 */

interface FAQItem {
  question: string;
  answer: string | string[];
}

interface FAQCategory {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  /** Estilo del tile (bg + color) para el icono de la categoría */
  tileClass: string;
  items: FAQItem[];
}

const faqData: FAQCategory[] = [
  {
    id: 'firma-electronica',
    title: 'Firma Electrónica',
    subtitle: 'Trámite, costos y renovación',
    icon: <KeyIcon className="h-4.5 w-4.5" />,
    tileClass:
      'bg-gradient-to-br from-[rgba(18,196,178,0.14)] to-[rgba(107,35,158,0.10)] text-[#6b239e]',
    items: [
      {
        question: '¿Cuánto tiempo toma el trámite de la firma electrónica cuando es gestionado por ustedes?',
        answer:
          'El trámite de la firma electrónica, cuando es gestionado con nuestro acompañamiento, suele completarse en un plazo aproximado de 3 a 7 días hábiles, dependiendo de la validación de la documentación y de la disponibilidad del contribuyente para completar el proceso de emisión de apoderado, declaración jurada y afiliación al PAC.',
      },
      {
        question: '¿Qué requisitos y documentación se necesitan para iniciar el trámite de la firma electrónica?',
        answer: [
          'Cédula o pasaporte vigente del contribuyente o representante legal.',
          'Aviso de operaciones, RUC actualizado.',
          'En caso de personas jurídicas, documentación que acredite la representación legal. En caso de persona natural, se debe solicitar la afiliación como contribuyente.',
          'Documento de apoderado emitido desde el sistema Etax del cliente.',
        ],
      },
      {
        question: '¿Cuál es el costo, vigencia y periodicidad de renovación de la firma electrónica?',
        answer:
          'La firma electrónica tiene un costo único de emisión con nosotros de $119.99, una vigencia de 2 años, y debe renovarse al finalizar dicho período. La renovación implica un nuevo pago y validación, ya que la firma tiene un vencimiento por normativa de seguridad.',
      },
      {
        question: '¿La firma electrónica queda a nombre del contribuyente?',
        answer:
          'Sí. La firma electrónica queda siempre a nombre del contribuyente o representante legal, no del proveedor del sistema de facturación. Es un activo digital personal e intransferible.',
      },
      {
        question: '¿Puede utilizarse la firma electrónica con otro proveedor?',
        answer:
          'Sí. La firma electrónica es independiente del sistema de facturación y puede utilizarse con cualquier proveedor que cumpla con los estándares y normativas de la DGI.',
      },
      {
        question: '¿Todos los facturadores necesitan contar con firma electrónica?',
        answer:
          'Sí. Todo contribuyente que emita facturación electrónica debe contar con una firma electrónica válida, ya que esta es necesaria para firmar digitalmente los documentos fiscales y garantizar su autenticidad e integridad.',
      },
    ],
  },
  {
    id: 'documento-apoderado',
    title: 'Documento de Apoderado',
    subtitle: 'Autorizaciones y descargas',
    icon: <DocumentTextIcon className="h-4.5 w-4.5" />,
    tileClass: 'bg-[rgba(245,153,29,0.14)] text-[#a15f0a]',
    items: [
      {
        question: '¿Qué es el documento de apoderado y para qué sirve?',
        answer:
          'El documento de apoderado es una autorización formal mediante la cual el contribuyente permite que un tercero (por ejemplo, un proveedor tecnológico) realice trámites ante la DGI en su nombre, como la habilitación del sistema de facturación electrónica.',
      },
      {
        question: '¿Cómo se descarga el documento de apoderado?',
        answer:
          'El documento se descarga directamente desde el portal de la DGI (sistema Etax), dentro del rubro de Factura Electrónica. Allí se desplegará la opción de "Apoderado de Retiro de Firma Electrónica (FEP)".',
      },
      {
        question: '¿Ustedes brindan acompañamiento para completar o validar el documento de apoderado?',
        answer:
          'Sí. Brindamos acompañamiento por medio del chat de soporte y una guía en formato PDF del paso a paso para completar correctamente el documento, asegurando que cumpla con los requisitos exigidos por la DGI y evitando rechazos o reprocesos.',
      },
    ],
  },
  {
    id: 'facturacion-dgi',
    title: 'Facturación Electrónica y DGI',
    subtitle: 'Rectificativas, resoluciones y recibidas',
    icon: <BuildingLibraryIcon className="h-4.5 w-4.5" />,
    tileClass: 'bg-[rgba(58,122,199,0.14)] text-[#3a7ac7]',
    items: [
      {
        question:
          'Si el contribuyente utilizaba el facturador gratuito, ¿el tipo de presentación debe ser Original o Rectificativa?',
        answer:
          'Debe ser rectificativa ya que la original se realizó al momento de integrarse al Facturador Gratuito de la DGI.',
      },
      {
        question: '¿Pueden proporcionar la resolución de la DGI correspondiente al sistema de facturación?',
        answer:
          'Sí. Se puede proporcionar la resolución oficial de la DGI que avala el sistema de facturación electrónica utilizado, como respaldo normativo para el contribuyente.',
      },
      {
        question: '¿Desde el portal se pueden visualizar las facturas recibidas?',
        answer:
          'Sí. El sistema permite visualizar tanto facturas emitidas como facturas recibidas, además de un balance general, facilitando el control administrativo y contable del contribuyente.',
      },
    ],
  },
  {
    id: 'uso-sistema',
    title: 'Uso del Sistema de Facturación',
    subtitle: 'Folios, personalización e impresión',
    icon: <ComputerDesktopIcon className="h-4.5 w-4.5" />,
    tileClass: 'bg-[rgba(107,35,158,0.10)] text-[#6b239e]',
    items: [
      {
        question: '¿Los folios de facturación son ilimitados?',
        answer:
          'Depende del plan contratado. Algunos planes tienen límites mensuales como el Plan Free, mientras que otros ofrecen folios ilimitados y funcionalidades adicionales.',
      },
      {
        question: '¿Las facturas emitidas incluyen el logo de la empresa?',
        answer:
          'Sí. El sistema permite personalizar las facturas, incluyendo el logo de la empresa y otros datos visuales dependiendo del plan.',
      },
      {
        question: '¿Es posible generar ofertas o cotizaciones y posteriormente convertirlas en facturas?',
        answer:
          'No. El sistema actualmente no permite la creación de cotizaciones, únicamente la emisión de facturación electrónica.',
      },
      {
        question:
          '¿Se puede modificar la numeración inicial de las facturas para dar continuidad a facturas emitidas previamente?',
        answer: 'Sí. Esta opción solo aplica a facturas de crédito.',
      },
      {
        question: '¿Qué tipos de notas de crédito y débito puedo emitir?',
        answer: [
          'Nota de Crédito Referente a una o Varias FE: corrige o anula parcialmente una factura electrónica ya emitida.',
          'Nota de Débito Referente a una o Varias FE: aumenta el monto de una factura electrónica ya emitida.',
          'Nota de Crédito Genérica: ajuste de crédito que no hace referencia a una factura electrónica específica.',
          'Nota de Débito Genérica: ajuste de débito que no hace referencia a una factura electrónica específica.',
        ],
      },
      {
        question: '¿Cómo emito una Nota de Crédito o de Débito sobre una factura ya emitida?',
        answer: [
          'Entra a Facturas Activas o Archivo Histórico y abre la factura que quieres corregir.',
          'En el detalle de la factura elige la acción Nota de Crédito o Nota de Débito.',
          'El asistente se abre con los datos del cliente ya cargados y con un paso adicional de Referencia, donde queda registrado el CUFE de la factura original. Son 6 pasos en vez de los 5 de una factura normal.',
          'Continúa con Detalles, Items, Notas y Revisar como en cualquier factura, ajustando los montos que correspondan.',
          'Importante: emítelas siempre desde el detalle de la factura original. Si creas una Nueva Factura y eliges a mano el tipo referenciado, el asistente no agrega el paso de Referencia y el documento saldría sin el CUFE de la factura que corrige.',
        ],
      },
      {
        question: '¿Cómo emito una Nota de Crédito o de Débito Genérica?',
        answer: [
          'Las genéricas no parten de una factura previa, así que se emiten desde Nueva Factura.',
          'En el Paso 2 (Detalles), en el campo Tipo de Documento, elige Nota de Crédito Genérica o Nota de Débito Genérica.',
          'El resto del proceso es igual al de una factura normal: son 5 pasos y no incluye el paso de Referencia, porque este documento no apunta a ninguna factura electrónica en particular.',
        ],
      },
      {
        question: '¿Cuándo uso una nota referenciada y cuándo una genérica?',
        answer:
          'Usa la referenciada cuando el ajuste corresponde a una factura electrónica concreta que ya emitiste: ahí queda el vínculo con su CUFE, que es lo que la DGI espera para relacionar ambos documentos. Usa la genérica solo para ajustes que no se pueden atribuir a una factura electrónica específica.',
      },
      {
        question: '¿Las facturas generadas desde el portal son compatibles con impresoras térmicas de 80 mm?',
        answer:
          'No. Solo se puede imprimir en formato tamaño carta y remitir directamente la factura por medio de correo electrónico.',
      },
    ],
  },
  {
    id: 'planes-funcionalidades',
    title: 'Planes y Funcionalidades',
    subtitle: 'Costos, cobertura y módulos',
    icon: <CurrencyDollarIcon className="h-4.5 w-4.5" />,
    tileClass: 'bg-[rgba(18,196,178,0.14)] text-[#0d9488]',
    items: [
      {
        question: '¿Cuál es el costo del sistema de facturación electrónica?',
        answer:
          'El costo varía según el plan seleccionado y las funcionalidades incluidas. Existen el Plan Free que es totalmente gratuito, el Plan Emprende que comprende un total de 750 folios anuales, el Plan Ilimitado y Premium que consta de folios ilimitados, y todos se encuentran diseñados para distintos volúmenes de facturación.',
      },
      {
        question: '¿Qué incluye cada uno de los planes disponibles?',
        answer: [
          'Cantidad de documentos emitidos.',
          'Usuarios.',
          'Reportes.',
          'Soporte técnico.',
          'Personalización.',
        ],
      },
      {
        question: '¿El plan ilimitado incluye módulo de inventario?',
        answer:
          'No. La plataforma no maneja módulos adicionales ni integraciones a otras plataformas por el momento.',
      },
      {
        question: '¿El sistema permite configurar fechas de vencimiento en las facturas?',
        answer: '',
      },
    ],
  },
  {
    id: 'demo-soporte',
    title: 'Demo y Soporte',
    subtitle: 'Canales, tickets y prioridad',
    icon: <ChatBubbleLeftRightIcon className="h-4.5 w-4.5" />,
    tileClass: 'bg-[rgba(184,80,58,0.12)] text-[#b8503a]',
    items: [
      {
        question: '¿El sistema cuenta con un demo para evaluación previa?',
        answer:
          'Al ser un portal completamente intuitivo y auto gestionable, no se brindan demostraciones personalizadas. El cliente puede inscribirse a nuestro plan gratuito y de ese modo generar las pruebas pertinentes y la visualización del portal en tiempo real.',
      },
      {
        question: '¿Qué tipo de soporte se ofrece a los clientes activos?',
        answer:
          'Se ofrece soporte técnico y funcional por medio de correo y chat, dependiendo también del tipo de plan se puede considerar un soporte prioritario.',
      },
      {
        question: '¿A través de qué canales se brinda el soporte?',
        answer:
          'El soporte se brinda a través de correo electrónico, chat y otros canales digitales, según el plan contratado.',
      },
      {
        question: '¿Cómo se gestionan las consultas o incidencias una vez el cliente está utilizando el sistema?',
        answer:
          'Las consultas e incidencias se gestionan mediante un flujo de atención por medio de tickets y personalizadamente por medio de llamada y chat de atención al cliente.',
      },
    ],
  },
];

/** Chips populares del hero — atajos que precargan la búsqueda. */
const POPULAR_QUERIES: { label: string; query: string }[] = [
  { label: 'Costo de la firma', query: 'costo' },
  { label: 'Requisitos DGI', query: 'requisitos' },
  { label: 'Apoderado', query: 'apoderado' },
];

function itemMatchesQuery(item: FAQItem, q: string): boolean {
  if (!q) return true;
  const query = q.toLowerCase();
  const inQuestion = item.question.toLowerCase().includes(query);
  const inAnswer = Array.isArray(item.answer)
    ? item.answer.some((a) => a.toLowerCase().includes(query))
    : item.answer.toLowerCase().includes(query);
  return inQuestion || inAnswer;
}

function FAQAccordionItem({
  item,
  index,
  isOpen,
  onToggle,
  testId,
}: {
  item: FAQItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  testId?: string;
}) {
  const paddedNum = String(index + 1).padStart(2, '0');
  return (
    <div
      className={
        'border-t border-dashed border-[#eef0f2] dark:border-secondary-700 ' +
        (isOpen
          ? 'bg-gradient-to-r from-accent-500/[.04] to-transparent'
          : '')
      }
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={testId}
        aria-expanded={isOpen}
        className="grid w-full cursor-pointer grid-cols-[32px_1fr_auto] items-center gap-3.5 px-7 py-[18px] text-left transition-colors hover:bg-[#fbfbfc] dark:hover:bg-secondary-900/40"
      >
        <span
          className={
            'font-mono text-[12px] font-bold uppercase tracking-[0.08em] ' +
            (isOpen ? 'text-[#0d9488]' : 'text-secondary-400')
          }
        >
          {paddedNum}
        </span>
        <span className="text-[15px] font-semibold leading-snug tracking-[-0.005em] text-ink-850 dark:text-white">
          {item.question}
        </span>
        <ChevronDownIcon
          className={
            'h-4 w-4 transition-transform duration-200 ' +
            (isOpen ? 'rotate-180 text-[#0d9488]' : 'text-secondary-400')
          }
        />
      </button>
      {isOpen && (
        <div className="pb-[22px] pl-[74px] pr-7 text-[14px] leading-[1.7] text-secondary-500 dark:text-secondary-400">
          {Array.isArray(item.answer) ? (
            <ul className="list-inside list-disc space-y-2">
              {item.answer.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          ) : item.answer ? (
            <p>{item.answer}</p>
          ) : (
            <p className="italic text-secondary-400">Respuesta pendiente de documentar.</p>
          )}
        </div>
      )}
    </div>
  );
}

function FAQCategorySection({
  category,
  index,
  searchQuery,
}: {
  category: FAQCategory;
  index: number;
  searchQuery: string;
}) {
  // Al buscar, abre por defecto los items que coinciden; sin búsqueda, abre solo
  // el primero de la primera sección para orientar al usuario.
  const initialOpen = useMemo(() => {
    if (searchQuery) {
      return new Set(
        category.items
          .map((item, i) => (itemMatchesQuery(item, searchQuery) ? i : -1))
          .filter((i) => i !== -1),
      );
    }
    return index === 0 ? new Set([0]) : new Set<number>();
  }, [category.items, index, searchQuery]);

  const [openItems, setOpenItems] = useState<Set<number>>(initialOpen);

  // Cuando cambia la búsqueda o la categoría, resincronizar los abiertos.
  const [lastQuery, setLastQuery] = useState(searchQuery);
  if (lastQuery !== searchQuery) {
    setLastQuery(searchQuery);
    setOpenItems(initialOpen);
  }

  const toggleItem = (i: number) => {
    const next = new Set(openItems);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setOpenItems(next);
  };

  const expandAll = () => setOpenItems(new Set(category.items.map((_, i) => i)));
  const collapseAll = () => setOpenItems(new Set());

  const paddedSection = String(index + 1).padStart(2, '0');

  return (
    <section
      id={category.id}
      className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_1px_2px_rgba(10,34,51,.03),0_12px_32px_-18px_rgba(10,34,51,.14)] dark:border-secondary-700 dark:bg-secondary-800"
    >
      <div className="flex flex-wrap items-center gap-3.5 border-b border-dashed border-[#eef0f2] px-7 py-[22px] dark:border-secondary-700">
        <span
          className={
            'inline-flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-[11px] ' +
            category.tileClass
          }
        >
          {category.icon}
        </span>
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-secondary-400">
            Sección {paddedSection}
          </div>
          <h2 className="mt-0.5 text-[20px] font-extrabold tracking-[-0.02em] text-ink-850 dark:text-white">
            {category.title}
          </h2>
        </div>
        <div className="ml-auto flex items-center gap-2.5 text-[13px] text-secondary-500 dark:text-secondary-400">
          <button
            type="button"
            onClick={expandAll}
            data-testid={`faq-expand-all-button-${category.id}`}
            className="rounded-md px-2 py-1 transition hover:bg-accent-500/[.06] hover:text-[#0d9488]"
          >
            Expandir todo
          </button>
          <span className="text-secondary-300 dark:text-secondary-600">|</span>
          <button
            type="button"
            onClick={collapseAll}
            data-testid={`faq-collapse-all-button-${category.id}`}
            className="rounded-md px-2 py-1 transition hover:bg-accent-500/[.06] hover:text-[#0d9488]"
          >
            Colapsar todo
          </button>
        </div>
      </div>

      {category.items.map((item, i) => (
        <FAQAccordionItem
          key={i}
          item={item}
          index={i}
          isOpen={openItems.has(i)}
          onToggle={() => toggleItem(i)}
          testId={`faq-question-toggle-${category.id}-${i}`}
        />
      ))}
    </section>
  );
}

export function FAQPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return faqData;
    return faqData
      .map((category) => ({
        ...category,
        items: category.items.filter((item) => itemMatchesQuery(item, searchTerm)),
      }))
      .filter((category) => category.items.length > 0);
  }, [searchTerm]);

  const totalQuestions = useMemo(
    () => faqData.reduce((sum, c) => sum + c.items.length, 0),
    [],
  );

  const handleQuickSearch = (query: string) => {
    setSearchTerm(query);
    // Scroll suave al principio del contenido si el usuario ya bajó por la lista.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleContactSupport = () => {
    // La pantalla anterior no llevaba a ninguna parte; abrimos un mailto como
    // fallback razonable — es reversible y no cambia lógica externa.
    window.location.href = 'mailto:soporte@hypernovalabs.com?subject=Soporte%20Facturación%20Electrónica';
  };

  return (
    <div className="max-w-[1500px] pb-12">
      {/* ============= HERO SEARCH ============= */}
      <div className="relative overflow-hidden rounded-[20px] bg-gradient-to-br from-[#0a2233] via-[#143b56] to-[#6b239e] px-8 py-10 text-white sm:px-11 sm:py-11">
        {/* Radial blobs decorativos */}
        <div className="pointer-events-none absolute -right-[10%] -top-[30%] h-[400px] w-[400px] rounded-full bg-[radial-gradient(circle,rgba(18,196,178,0.30),transparent_60%)]" />
        <div className="pointer-events-none absolute -bottom-[40%] -left-[5%] h-[300px] w-[300px] rounded-full bg-[radial-gradient(circle,rgba(107,35,158,0.35),transparent_60%)]" />

        <div className="relative z-10 max-w-[700px]">
          <div className="inline-flex items-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.24em] text-[#12c4b2]">
            <span className="h-px w-[22px] bg-[#12c4b2]" />
            Centro de ayuda
          </div>
          <h1 className="mt-3 text-[40px] font-extrabold leading-[1.1] tracking-[-0.035em] text-white">
            Preguntas Frecuentes
          </h1>
          <p className="mt-3 max-w-[560px] text-[15px] text-white/70">
            Encuentra respuestas a las consultas más comunes sobre Facturación Electrónica
          </p>

          <div className="relative mt-6 rounded-[14px] border border-white/10 bg-white shadow-[0_20px_40px_-20px_rgba(0,0,0,.3)] transition focus-within:shadow-[0_24px_50px_-20px_rgba(18,196,178,0.4)]">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#6b239e]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="faq-search-input"
              placeholder="Buscar en las preguntas frecuentes…"
              className="w-full rounded-[14px] border-0 bg-transparent py-4 pl-[54px] pr-16 text-[15px] text-ink-850 outline-none placeholder:text-[#a1a1aa] focus:ring-0"
            />
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rounded-md border border-[#ececec] px-2.5 py-1 font-mono text-[12px] text-secondary-400">
              ⌘K
            </span>
          </div>

          {/* Populares */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-white/50">
              Populares:
            </span>
            {POPULAR_QUERIES.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => handleQuickSearch(p.query)}
                className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[13px] font-medium text-white transition hover:border-white/30 hover:bg-white/20"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ============= LAYOUT: sidebar categorías + contenido ============= */}
      <div className="mt-7 grid grid-cols-1 items-start gap-6 xl:grid-cols-[300px_minmax(0,1fr)] xl:gap-8">
        {/* CATEGORY SIDEBAR */}
        <aside className="xl:sticky xl:top-6">
          <div className="rounded-2xl border border-[#ececec] bg-white p-2 shadow-[0_1px_2px_rgba(10,34,51,.03),0_12px_32px_-18px_rgba(10,34,51,.14)] dark:border-secondary-700 dark:bg-secondary-800">
            <div className="px-3 pb-2 pt-3 font-mono text-[11px] uppercase tracking-[0.24em] text-secondary-400">
              Categorías
            </div>
            {faqData.map((cat) => {
              const count = cat.items.length;
              const visibleCount = filteredCategories.find((c) => c.id === cat.id)?.items.length ?? 0;
              const active = searchTerm
                ? visibleCount > 0
                : cat.id === faqData[0]?.id;
              return (
                <a
                  key={cat.id}
                  href={`#${cat.id}`}
                  className={
                    'grid grid-cols-[36px_1fr_auto] items-center gap-3 rounded-[10px] px-3 py-3 transition-colors ' +
                    (active
                      ? 'bg-gradient-to-br from-accent-500/10 to-[rgba(107,35,158,0.06)]'
                      : 'hover:bg-secondary-50 dark:hover:bg-secondary-900/50')
                  }
                >
                  <span
                    className={
                      'inline-flex h-9 w-9 items-center justify-center rounded-[9px] ' + cat.tileClass
                    }
                  >
                    {cat.icon}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-bold tracking-[-0.01em] text-ink-850 dark:text-white">
                      {cat.title}
                    </div>
                    <div className="mt-0.5 truncate text-[12px] text-secondary-400">
                      {cat.subtitle}
                    </div>
                  </div>
                  <span
                    className={
                      'rounded-full px-2 py-[2px] font-mono text-[12px] font-bold ' +
                      (active
                        ? 'bg-ink-850 text-white'
                        : 'bg-secondary-100 text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300')
                    }
                  >
                    {searchTerm ? `${visibleCount}/${count}` : count}
                  </span>
                </a>
              );
            })}

            {/* Total counter */}
            <div className="mt-2 border-t border-dashed border-[#eef0f2] p-3 dark:border-secondary-700">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.20em] text-secondary-400">
                    Total preguntas
                  </div>
                  <div className="mt-0.5 text-[22px] font-extrabold tracking-[-0.02em] text-ink-850 dark:text-white">
                    {totalQuestions}
                  </div>
                </div>
                <QuestionMarkCircleIcon className="h-8 w-8 text-[#0d9488] opacity-70" />
              </div>
            </div>
          </div>
        </aside>

        {/* FAQ COLUMN */}
        <div className="flex min-w-0 flex-col gap-5">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <FAQCategorySection
                key={category.id}
                category={category}
                index={faqData.findIndex((c) => c.id === category.id)}
                searchQuery={searchTerm}
              />
            ))
          ) : (
            <div className="rounded-2xl border border-[#ececec] bg-white px-6 py-14 text-center shadow-[0_1px_2px_rgba(10,34,51,.03),0_12px_32px_-18px_rgba(10,34,51,.14)] dark:border-secondary-700 dark:bg-secondary-800">
              <MagnifyingGlassIcon className="mx-auto h-10 w-10 text-secondary-300" />
              <h3 className="mt-3 text-[15px] font-bold text-ink-850 dark:text-white">
                Sin resultados
              </h3>
              <p className="mt-1 text-[14px] text-secondary-500 dark:text-secondary-400">
                No se encontraron preguntas para “{searchTerm}”.
              </p>
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="mt-4 rounded-md px-3 py-1.5 text-[13px] font-semibold text-[#0d9488] transition hover:bg-accent-500/[.06]"
              >
                Limpiar búsqueda
              </button>
            </div>
          )}

          {/* Help card */}
          <div className="grid grid-cols-1 items-center gap-4 rounded-2xl border border-accent-500/25 bg-gradient-to-br from-accent-500/[.08] to-[rgba(107,35,158,0.04)] px-7 py-6 sm:grid-cols-[44px_1fr_auto]">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-accent-500/25 bg-white text-[#0d9488]">
              <ChatBubbleOvalLeftIcon className="h-5 w-5" />
            </span>
            <div>
              <div className="text-[15px] font-bold tracking-[-0.01em] text-ink-850 dark:text-white">
                ¿No encontraste tu respuesta?
              </div>
              <div className="mt-1 text-[14px] text-secondary-500 dark:text-secondary-400">
                Nuestro equipo de soporte está disponible en horario hábil.
              </div>
            </div>
            <button
              type="button"
              onClick={handleContactSupport}
              className="inline-flex items-center gap-2 rounded-[10px] bg-ink-850 px-[22px] py-3 text-[14px] font-semibold text-white transition hover:bg-[#142d40]"
            >
              <PhoneIcon className="h-3.5 w-3.5" />
              Contactar soporte
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
